import React from 'react';
import { Table, Tag, Button, Space, Tooltip, Card, Empty, Pagination, Typography } from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  FormOutlined,
  FileTextOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { Essay } from '../types';
import StatusTag from './StatusTag';
import moment from 'moment';
import 'moment/locale/zh-cn';
import { getTopicFromEssay } from '../utils/topicHelper';
import './EssayListTable.css';
import {
  buildFilters,
  compareDate,
  compareEssayStatus,
  compareNumber,
  compareText,
  createValueFilter,
  getEssayStatusLabel,
  getScoreBand,
  SCORE_BAND_FILTERS,
} from '../utils/tableHelpers';

moment.locale('zh-cn');

const { Text } = Typography;

interface EssayListTableProps {
  essays: Essay[];
  loading: boolean;
  pagination?: any;
  onChange?: (pagination: any, filters?: Record<string, any>, sorter?: any) => void;
  onDelete?: (id: number) => void;
  showActions?: boolean;
  remote?: boolean;
  filterValues?: Record<string, any>;
  sortState?: {
    columnKey?: string;
    order?: 'ascend' | 'descend' | null;
  };
}

const EssayListTable: React.FC<EssayListTableProps> = ({
  essays,
  loading,
  pagination,
  onChange,
  onDelete,
  showActions = true,
  remote = false,
  filterValues,
  sortState,
}) => {
  const navigate = useNavigate();


  // 获取操作按钮
  const getActionButton = (essay: Essay) => {
    // 已完成且有批改结果
    if (essay.status === 'completed' && essay.correction) {
      return {
        text: '查看结果',
        icon: <EyeOutlined />,
        type: 'link' as const,
        onClick: () => navigate(`/essay/${essay.id}/result`)
      };
    }
    
    // 已识别文本，可继续批改
    if (essay.corrected_text && essay.status !== 'completed') {
      return {
        text: '继续批改',
        icon: <FormOutlined />,
        type: 'primary' as const,
        onClick: () => navigate(`/essay-wizard/continue/${essay.id}?step=3`)
      };
    }
    
    // 已上传文件，需要确认文本
    if (essay.original_file && !essay.corrected_text) {
      return {
        text: '确认文本',
        icon: <FileTextOutlined />,
        type: 'primary' as const,
        onClick: () => navigate(`/essay-wizard/continue/${essay.id}?step=2`)
      };
    }
    
    // 处理中
    if (essay.status === 'processing' || essay.status === 'correcting') {
      return {
        text: '处理中',
        icon: <SyncOutlined spin />,
        type: 'default' as const,
        disabled: true
      };
    }

    // 没有上传文件，直接跳转到上传步骤
    if (!essay.original_file) {
      return {
        text: '上传作文',
        icon: <FormOutlined />,
        type: 'primary' as const,
        onClick: () => navigate(`/essay-wizard/continue/${essay.id}?step=1`)
      };
    }

    // 默认查看（有文件但状态未知的情况）
    return {
      text: '查看',
      icon: <EyeOutlined />,
      type: 'link' as const,
      onClick: () => navigate(`/essay/${essay.id}/result`)
    };
  };

  const columns: ColumnsType<Essay> = [
    {
      title: '标题',
      key: 'title',
      ellipsis: true,
      width: 200,
      sorter: remote ? true : compareText((record: Essay) => getTopicFromEssay(record)?.title),
      filters: buildFilters(essays, (record: Essay) => getTopicFromEssay(record)?.title, {
        includeEmpty: true,
        emptyText: '未设置',
      }),
      onFilter: remote ? undefined : createValueFilter((record: Essay) => getTopicFromEssay(record)?.title),
      filteredValue: remote ? (filterValues?.title || null) : undefined,
      sortOrder: remote && sortState?.columnKey === 'title' ? sortState.order : null,
      filterSearch: true,
      render: (_: string, record: Essay) => getTopicFromEssay(record)?.title || '未设置',
    },
    {
      title: '学生',
      key: 'student',
      width: 100,
      sorter: remote ? true : compareText((record: Essay) => record.student?.name),
      filters: buildFilters(essays, (record: Essay) => record.student?.name, {
        includeEmpty: true,
        emptyText: '未分配学生',
      }),
      onFilter: remote ? undefined : createValueFilter((record: Essay) => record.student?.name),
      filteredValue: remote ? (filterValues?.student || null) : undefined,
      sortOrder: remote && sortState?.columnKey === 'student' ? sortState.order : null,
      filterSearch: true,
      render: (_: string, record: Essay) => record.student?.name || '-',
    },
    {
      title: '班级',
      key: 'class',
      width: 100,
      sorter: remote ? true : compareText((record: Essay) => record.class?.name),
      filters: buildFilters(essays, (record: Essay) => record.class?.name, {
        includeEmpty: true,
        emptyText: '未分配班级',
      }),
      onFilter: remote ? undefined : createValueFilter((record: Essay) => record.class?.name),
      filteredValue: remote ? (filterValues?.class || null) : undefined,
      sortOrder: remote && sortState?.columnKey === 'class' ? sortState.order : null,
      filterSearch: true,
      render: (_: string, record: Essay) => record.class?.name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: remote ? true : compareEssayStatus((record: Essay) => record.status),
      filters: buildFilters(essays, (record: Essay) => record.status, {
        labelGetter: (value) => getEssayStatusLabel(value),
      }),
      onFilter: remote ? undefined : createValueFilter((record: Essay) => record.status),
      filteredValue: remote ? (filterValues?.status || null) : undefined,
      sortOrder: remote && sortState?.columnKey === 'status' ? sortState.order : null,
      filterSearch: true,
      render: (status: string, record: Essay) => <StatusTag status={record.status} />,
    },
    {
      title: '分数',
      key: 'score',
      width: 80,
      sorter: remote ? true : compareNumber((record: Essay) => record.correction?.total_score),
      filters: SCORE_BAND_FILTERS,
      onFilter: remote ? undefined : ((value: boolean | string | number | bigint, record: Essay) => (
        getScoreBand(record.correction?.total_score) === String(value)
      )),
      filteredValue: remote ? (filterValues?.score || null) : undefined,
      sortOrder: remote && sortState?.columnKey === 'score' ? sortState.order : null,
      render: (text: string, record: Essay) => {
        if (record.correction?.total_score) {
          const score = record.correction.total_score;
          const topic = getTopicFromEssay(record);
          const maxScore = topic?.essay_type === 'application' ? 15 : 25;
          return (
            <Tag color={score >= maxScore * 0.8 ? 'green' : score >= maxScore * 0.6 ? 'orange' : 'red'}>
              {score}/{maxScore}
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: '字数',
      dataIndex: 'word_count',
      key: 'word_count',
      width: 80,
      sorter: remote ? true : compareNumber((record: Essay) => record.word_count),
      sortOrder: remote && sortState?.columnKey === 'word_count' ? sortState.order : null,
      render: (count: number) => count || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      sorter: remote ? true : compareDate((record: Essay) => record.created_at),
      sortOrder: remote && sortState?.columnKey === 'created_at' ? sortState.order : null,
      render: (time: string) => moment(time).format('YYYY/MM/DD HH:mm:ss'),
    },
  ];

  if (showActions) {
    columns.push({
      title: '操作',
      key: 'action',
      width: 150,
      render: (text: string, record: Essay) => {
        const actionConfig = getActionButton(record);
        return (
          <Space size="small">
            <Button
              type={actionConfig.type}
              size="small"
              icon={actionConfig.icon}
              onClick={actionConfig.onClick}
              disabled={actionConfig.disabled}
            >
              {actionConfig.text}
            </Button>
            {onDelete && (
              <Tooltip title="删除">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    });
  }

  const renderScoreTag = (essay: Essay) => {
    if (essay.correction?.total_score === undefined || essay.correction?.total_score === null) {
      return <Text type="secondary">未评分</Text>;
    }

    const score = essay.correction.total_score;
    const topic = getTopicFromEssay(essay);
    const maxScore = topic?.essay_type === 'application' ? 15 : 25;

    return (
      <Tag color={score >= maxScore * 0.8 ? 'green' : score >= maxScore * 0.6 ? 'orange' : 'red'}>
        {score}/{maxScore}
      </Tag>
    );
  };

  const handleMobilePageChange = (page: number, pageSize: number) => {
    if (!pagination || pagination === false) {
      return;
    }

    if (typeof pagination.onChange === 'function') {
      pagination.onChange(page, pageSize);
      return;
    }

    onChange?.(
      { ...pagination, current: page, pageSize },
      filterValues,
      { columnKey: sortState?.columnKey, order: sortState?.order }
    );
  };

  const renderMobileList = () => {
    if (!loading && essays.length === 0) {
      return (
        <div className="essay-mobile-empty">
          <Empty description="暂无作文记录" />
        </div>
      );
    }

    return (
      <>
        <div className="essay-mobile-list">
          {essays.map((essay) => {
            const topic = getTopicFromEssay(essay);
            const actionConfig = getActionButton(essay);

            return (
              <Card
                key={essay.id}
                className="essay-mobile-card"
                loading={loading}
                size="small"
                title={
                  <div className="essay-mobile-card-title">
                    <FileTextOutlined />
                    <span>{topic?.title || '未设置题目'}</span>
                  </div>
                }
                extra={<StatusTag status={essay.status} />}
              >
                <div className="essay-mobile-meta">
                  <div>
                    <Text type="secondary">学生</Text>
                    <Text strong>{essay.student?.name || '-'}</Text>
                  </div>
                  <div>
                    <Text type="secondary">班级</Text>
                    <Text strong>{essay.class?.name || '-'}</Text>
                  </div>
                  <div>
                    <Text type="secondary">分数</Text>
                    {renderScoreTag(essay)}
                  </div>
                  <div>
                    <Text type="secondary">字数</Text>
                    <Text strong>{essay.word_count || '-'}</Text>
                  </div>
                </div>

                <div className="essay-mobile-time">
                  {essay.created_at ? moment(essay.created_at).format('YYYY/MM/DD HH:mm') : '-'}
                </div>

                {showActions && (
                  <Space className="essay-mobile-actions" size="small">
                    <Button
                      type={actionConfig.type}
                      icon={actionConfig.icon}
                      onClick={actionConfig.onClick}
                      disabled={actionConfig.disabled}
                      block
                    >
                      {actionConfig.text}
                    </Button>
                    {onDelete && (
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onDelete(essay.id)}
                        block
                      >
                        删除
                      </Button>
                    )}
                  </Space>
                )}
              </Card>
            );
          })}
        </div>

        {pagination && pagination !== false && (
          <Pagination
            className="essay-mobile-pagination"
            current={pagination.current || 1}
            pageSize={pagination.pageSize || 10}
            total={pagination.total || essays.length}
            showSizeChanger={false}
            responsive
            onChange={handleMobilePageChange}
          />
        )}
      </>
    );
  };

  return (
    <>
      <div className="essay-table-desktop">
        <Table
          columns={columns}
          dataSource={essays}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={onChange}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: '暂无作文记录',
          }}
        />
      </div>
      <div className="essay-table-mobile">
        {renderMobileList()}
      </div>
    </>
  );
};

export default EssayListTable;
